import classNames from 'classnames';
import { BlockEmbed } from 'quill/blots/block';
import Embed from 'quill/blots/embed';
import type Header from 'quill/formats/header';
import React from 'react';
import { createPortal } from 'react-dom';
import SVGIcon from '../components/SVGIcon';
import Tooltip from '../components/Tooltip';
import alertIcon from '../icons/alert.svg';
import styles from './BlotErrorBoundaryFallback.module.scss';
import type { PortalBlot } from './QuillEditor';

interface BlotErrorBoundaryFallbackProps {
  blot: Embed | BlockEmbed | Header | PortalBlot;
  isCausedByTranslation: boolean;
}

const BlotErrorBoundaryFallback = ({
  blot,
  isCausedByTranslation,
}: BlotErrorBoundaryFallbackProps) => {
  const title = 'Uh oh!';
  const message = `This ${blot.statics.blotName} is having issues loading. ${
    isCausedByTranslation
      ? 'The error is most likely caused by a translation tool.'
      : 'The error has been logged and we’ll be on it!'
  }`;

  if (blot instanceof BlockEmbed) {
    return createPortal(
      <div className={classNames(styles.container, styles.block)}>
        <SVGIcon className={styles.icon}>{alertIcon}</SVGIcon>
        <div className={styles.content}>
          {title}
          <div className={styles.message}>{message}</div>
        </div>
      </div>,
      blot.domNode,
    );
  }

  if (blot instanceof Embed) {
    return createPortal(
      <Tooltip
        trigger="hover"
        tooltip={message}
        placement="top"
        popOverPortal={document.body}
      >
        <div className={styles.container}>
          <SVGIcon inline className={styles.icon}>
            {alertIcon}
          </SVGIcon>
          <div className={styles.content}>{title}</div>
        </div>
      </Tooltip>,
      blot.contentNode,
    );
  }

  return null;
};

export default BlotErrorBoundaryFallback;
